import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, ShieldCheck, Sparkles, CheckCircle2, Lock } from 'lucide-react';
import { api } from '../lib/api';
import { logger } from '../lib/logger';
import { toast } from 'sonner';
import PhoneInput from './PhoneInput';
import { trackLead, trackForm1PopupOpen } from '../lib/pixel';

const STORAGE_PHASE1 = 'gp_vsl_phase1';
const STORAGE_LEAD_ID = 'gp_vsl_lead_id';

/**
 * VslForm1Popup — popup obbligatorio mostrato a 30s sopra al VSL video.
 *
 * Cattura SOLO 4 campi base (Nome, Cognome, Email, Telefono) tramite
 * `POST /api/public/vsl-gate-lead`. Il backend:
 *   • crea/aggiorna il lead con tag `vsl_gate_phase1`
 *   • lancia `on_form1_submitted` → assegna l'Associate e invia DM Telegram
 *
 * Una volta inviato con successo:
 *   • salva STORAGE_PHASE1 con i dati (così il QualificationModal può prefillare)
 *   • salva STORAGE_LEAD_ID con l'id del lead creato
 *   • chiama `onSubmitted()` per chiudere il popup nel VslGate
 *
 * Il popup è BLOCCANTE — niente "X" di chiusura, niente click-outside.
 */
export default function VslForm1Popup({ open, onSubmitted }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_e164: '',
    phone_country_code: 'IT',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const firstInputRef = useRef(null);

  // Auto-focus first input on open + lock background scroll
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      try { firstInputRef.current?.focus(); } catch (err) { logger.debug('VslForm1Popup: focus failed', err); }
    }, 200);
    // ★ Meta Pixel: track popup impression (engagement signal, NOT a lead).
    // This drives retargeting audiences like "saw the popup but didn't fill".
    try { trackForm1PopupOpen({ trigger: 'auto_30s' }); } catch (err) {
      logger.debug('VslForm1Popup: trackForm1PopupOpen failed', err);
    }
    return () => clearTimeout(t);
  }, [open]);

  // Try to prefill from previously saved phase1 / draft data.
  // setForm calls are deferred via setTimeout(0) so they run asynchronously
  // (avoids the `react-hooks/set-state-in-effect` rule and any potential
  // double-render in concurrent mode).
  useEffect(() => {
    if (!open) return;
    const tid = setTimeout(() => {
      try {
        const raw = localStorage.getItem(STORAGE_PHASE1);
        if (raw && raw !== '1') {
          const p = JSON.parse(raw);
          if (p && typeof p === 'object') {
            setForm((f) => ({
              first_name: p.first_name || f.first_name,
              last_name: p.last_name || f.last_name,
              email: p.email || f.email,
              phone_e164: p.phone_e164 || p.phone || f.phone_e164,
              phone_country_code: p.phone_country_code || f.phone_country_code,
            }));
          }
        }
      } catch (err) {
        logger.debug('VslForm1Popup: prefill failed', err);
      }
      // Also try organic prefill (magic link)
      try {
        const organicRaw = sessionStorage.getItem('gp_organic_prefill');
        if (organicRaw) {
          const o = JSON.parse(organicRaw);
          if (o && typeof o === 'object') {
            const fullName = (o.name || '').trim();
            const parts = fullName.split(/\s+/);
            const firstName = parts.shift() || '';
            const lastName = parts.join(' ');
            setForm((f) => ({
              first_name: f.first_name || firstName,
              last_name: f.last_name || lastName,
              email: f.email || o.email || '',
              phone_e164: f.phone_e164 || o.phone || '',
              phone_country_code: f.phone_country_code || 'IT',
            }));
          }
        }
      } catch (err) {
        logger.debug('VslForm1Popup: organic prefill failed', err);
      }
    }, 0);
    return () => clearTimeout(tid);
  }, [open]);

  const isValid = (
    form.first_name.trim().length > 1 &&
    form.last_name.trim().length > 1 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) &&
    !!form.phone_e164 && /^\+\d{1,4}\d{6,}$/.test(form.phone_e164)
  );

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      // Collect UTM parameters
      const utm = {};
      try {
        const qs = new URLSearchParams(window.location.search);
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gp_track']
          .forEach((k) => { const v = qs.get(k); if (v) utm[k] = v; });
      } catch (err) {
        logger.debug('VslForm1Popup: UTM parse failed', err);
      }

      const r = await api.post('/public/vsl-gate-lead', {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone_e164 || undefined,
        consent_privacy: true,
        utm: Object.keys(utm).length ? utm : undefined,
        page_context: 'vsl_gate_30s_popup',
        // ★ Affiliate attribution \u2014 forwarded if /aff/:code was visited earlier.
        affiliate_code: (() => {
          try { return sessionStorage.getItem('gp_affiliate_code') || undefined; }
          catch (err) { logger.debug('best-effort op failed', err); return undefined; }
        })(),
      });

      const leadId = r?.data?.lead_id;
      // ★ Meta Pixel — Lead event (key signal for Ads Manager optimization)
      try {
        trackLead({
          leadId,
          source: 'vsl_gate_form1_popup_30s',
          country: form.phone_country_code,
        });
      } catch (err) {
        logger.debug('VslForm1Popup: pixel trackLead failed', err);
      }
      // Persist phase1 payload so QualificationModal can prefill later
      try {
        localStorage.setItem(STORAGE_PHASE1, JSON.stringify({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          phone: form.phone_e164,
          phone_e164: form.phone_e164,
          phone_country_code: form.phone_country_code,
        }));
        if (leadId) localStorage.setItem(STORAGE_LEAD_ID, leadId);
      } catch (err) {
        logger.debug('VslForm1Popup: localStorage save failed', err);
      }

      setSuccess(true);
      toast.success(t('vslForm1.successToast', 'Perfetto! Continua a guardare il video.'));

      // Brief success animation, then close
      setTimeout(() => {
        onSubmitted?.(leadId);
      }, 900);
    } catch (e) {
      const msg = e?.response?.data?.detail || t('vslForm1.errorSubmit', 'Errore. Riprova tra poco.');
      setError(typeof msg === 'string' ? msg : t('vslForm1.errorSubmit', 'Errore. Riprova tra poco.'));
      setSubmitting(false);
    }
  }, [form, isValid, submitting, onSubmitted, t]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-3 sm:p-6 bg-black/85 backdrop-blur-md animate-in fade-in duration-300 overflow-y-auto"
      data-testid="vsl-form1-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vsl-form1-title"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onPointerDown={(e) => { e.stopPropagation(); }}
      onMouseDown={(e) => { e.stopPropagation(); }}
      onTouchStart={(e) => { e.stopPropagation(); }}
      onWheel={(e) => { e.stopPropagation(); }}
      onScroll={(e) => { e.stopPropagation(); }}
    >
      <div
        className="w-full max-w-md bg-[var(--surface-1,#fbfaf6)] text-[hsl(var(--primary,158_45%_15%))] rounded-2xl shadow-2xl border border-[var(--gp-gold,#c9a96a)]/30 overflow-hidden animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-5 pt-5 pb-3 bg-gradient-to-br from-[var(--gp-forest,#0e3a2c)] to-[var(--gp-forest-dark,#082019)] text-white">
          <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-[var(--gp-gold-soft,#e6d4a8)]">
            <Sparkles className="w-3 h-3" />
            {t('vslForm1.eyebrow', "SBLOCCA L'ACCESSO COMPLETO")}
          </div>
          <h3
            id="vsl-form1-title"
            className="font-serif text-xl md:text-2xl mt-1.5 leading-tight"
          >
            {t('vslForm1.title', 'Lascia i tuoi dati per continuare')}
          </h3>
          <p className="text-xs md:text-sm text-white/75 mt-1.5 leading-relaxed">
            {t('vslForm1.subtitle1', 'Un nostro Senior Associate ti contatterà solo se vorrai approfondire.')}
            <br className="hidden sm:inline" /> {t('vslForm1.subtitle2', 'Il video continua mentre compili.')}
          </p>
        </div>

        {success ? (
          // ── SUCCESS STATE ──────────────────────────────────────────────
          <div
            className="px-6 py-10 flex flex-col items-center justify-center text-center"
            data-testid="vsl-form1-popup-success"
          >
            <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mb-3 animate-in zoom-in duration-300">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <div className="font-serif text-lg text-[hsl(var(--primary,158_45%_15%))]">
              {t('vslForm1.thankYou', 'Grazie {{name}}!', { name: form.first_name })}
            </div>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-xs">
              {t('vslForm1.successBody', 'Ti abbiamo registrato. Continua a guardare il video — il tasto per accedere al sito apparirà a breve.')}
            </p>
          </div>
        ) : (
          // ── FORM STATE ─────────────────────────────────────────────────
          <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3" autoComplete="on">
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label htmlFor="vsl-f1-first" className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                  {t('vslForm1.firstName', 'Nome')}
                </label>
                <Input
                  ref={firstInputRef}
                  id="vsl-f1-first"
                  type="text"
                  autoComplete="given-name"
                  value={form.first_name}
                  onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                  placeholder={t('vslForm1.firstNamePlaceholder', 'Mario')}
                  className="mt-1 h-10 text-sm"
                  required
                  data-testid="vsl-form1-first-name"
                />
              </div>
              <div>
                <label htmlFor="vsl-f1-last" className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                  {t('vslForm1.lastName', 'Cognome')}
                </label>
                <Input
                  id="vsl-f1-last"
                  type="text"
                  autoComplete="family-name"
                  value={form.last_name}
                  onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                  placeholder={t('vslForm1.lastNamePlaceholder', 'Rossi')}
                  className="mt-1 h-10 text-sm"
                  required
                  data-testid="vsl-form1-last-name"
                />
              </div>
            </div>

            <div>
              <label htmlFor="vsl-f1-email" className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                {t('vslForm1.email', 'Email')}
              </label>
              <Input
                id="vsl-f1-email"
                type="email"
                autoComplete="email"
                inputMode="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder={t('vslForm1.emailPlaceholder', 'mario.rossi@esempio.it')}
                className="mt-1 h-10 text-sm"
                required
                data-testid="vsl-form1-email"
              />
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                {t('vslForm1.phone', 'Telefono')}
              </label>
              <div className="mt-1">
                <PhoneInput
                  value={form.phone_e164}
                  onChange={(ev) => setForm((f) => ({
                    ...f,
                    phone_e164: ev.e164 || '',
                    phone_country_code: ev.country_code || f.phone_country_code,
                  }))}
                  testIdPrefix="vsl-form1-phone"
                  required
                />
              </div>
            </div>

            {error && (
              <div
                className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                data-testid="vsl-form1-error"
                role="alert"
              >
                {error}
              </div>
            )}

            <div className="pt-1.5">
              <Button
                type="submit"
                disabled={!isValid || submitting}
                className="w-full h-11 text-sm font-semibold bg-[var(--gp-gold,#c9a96a)] text-[var(--gp-forest,#0e3a2c)] hover:bg-[var(--gp-gold-soft,#e6d4a8)] disabled:opacity-50 shadow-md"
                data-testid="vsl-form1-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('vslForm1.submitting', 'Invio in corso...')}
                  </>
                ) : (
                  <>{t('vslForm1.submit', 'Sblocca e continua a guardare')}</>
                )}
              </Button>
            </div>

            <div className="flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground pt-1">
              <Lock className="w-3 h-3" />
              <span>{t('vslForm1.protected', 'Dati protetti')} · </span>
              <ShieldCheck className="w-3 h-3" />
              <span>{t('vslForm1.noSpam', 'Niente spam')}</span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
